import { useDragLayer, type XYCoord } from "react-dnd";
import moment from "moment-timezone";
import DayViewEventDisplay from "../../../Content/Grid/Body/DayView/EventDisplay";
import CustomViewEventDisplay from "../../../Content/Grid/Body/CustomView/EventDisplay";
import { useFilters } from "../../../../../../utils/hooks/useFilters";
import { EVENT_ROW_HEIGHT } from "../../../constants/height";

const POINTER_INSET = 16;

// Where this layer draws the preview's left edge - the source node is the wide card in the
// unassigned panel, so its offset doesn't match what the user sees over the grid.
export const getUnassignedPreviewOffset = (clientOffset: XYCoord | null): XYCoord | null => {
    if (!clientOffset) return null;
    return { x: clientOffset.x - POINTER_INSET, y: clientOffset.y };
};

function CustomDragLayer({ cellWidth }: { cellWidth: number }) {
    const [filters] = useFilters();

    const { isDragging, item, itemType, clientOffset } = useDragLayer((monitor) => ({
        item: monitor.getItem(),
        itemType: monitor.getItemType(),
        clientOffset: monitor.getClientOffset(),
        isDragging: monitor.isDragging()
    }));

    if (!isDragging || itemType !== "UNASSIGNED_EVENT" || !clientOffset || !(cellWidth > 0)) {
        return null;
    }

    const isDayView = filters?.mode === "day";
    const durationMinutes = moment(item?.endDate).diff(moment(item?.startDate), "minutes");
    const width = isDayView
        ? Math.max(40, (durationMinutes / 60) * cellWidth)
        : cellWidth - 9;
    const style = { position: "absolute" as const, top: 0, left: 0, width, height: EVENT_ROW_HEIGHT };

    return (
        <div
            className="tw-fixed tw-top-0 tw-left-0 tw-z-50 tw-pointer-events-none tw-opacity-90"
            style={{ width, height: EVENT_ROW_HEIGHT, transform: `translate(${clientOffset.x - POINTER_INSET}px, ${clientOffset.y}px) translateY(-50%)` }}
        >
            {isDayView
                ? <DayViewEventDisplay event={item} style={style} cellWidth={cellWidth} />
                : <CustomViewEventDisplay event={item} style={style} />}
        </div>
    );
}

export default CustomDragLayer;
